import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { Settings as SettingsIcon, MessageCircle, Mail, Save, TestTube, Loader2, Lock, Eye, EyeOff, X, CreditCard, AlertCircle, Plug, ArrowRight } from 'lucide-react';

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentWorkspace, refreshWorkspaces, subscriptionInfo } = useWorkspace();
  const { changePassword } = useAuth();
  const [whatsappSettings, setWhatsappSettings] = useState({
    phoneNumberId: '',
    accessToken: '',
    verifyToken: '',
    businessAccountId: '',
    whatsappAccountId: '',
    businessPhoneNumber: ''
  });

  const [generatedWebhookUrl, setGeneratedWebhookUrl] = useState('');

  const [emailSettings, setEmailSettings] = useState({
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: '',
    adminEmails: ''
  });

  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl === 'plans' ? 'plans' : 'whatsapp');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const [plans, setPlans] = useState([]);
  const [subscriptionCurrent, setSubscriptionCurrent] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // Password update state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleWhatsappChange = (field, value) => {
    setWhatsappSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailChange = (field, value) => {
    setEmailSettings(prev => ({ ...prev, [field]: value }));
  };

  const generateWebhookUrl = useCallback(() => {
    if (!currentWorkspace?.id) return;

    // Always generate webhook URL with production backend URL
    const baseUrl = 'https://leads-automater-api.nowgray.live';
    const webhookUrl = `${baseUrl}/api/whatsapp/webhook/${currentWorkspace.id}`;

    console.log('🔧 Generating webhook URL:', {
      baseUrl: baseUrl,
      workspaceId: currentWorkspace.id,
      webhookUrl: webhookUrl
    });

    setGeneratedWebhookUrl(webhookUrl);
  }, [currentWorkspace]);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      // Load WhatsApp settings
      const waResponse = await apiClient.get('/api/settings/whatsapp', { headers });
      if (waResponse.data.settings) {
        const settings = waResponse.data.settings;
        setWhatsappSettings({
          phoneNumberId: settings.phone_number_id || '',
          accessToken: settings.access_token || '',
          verifyToken: settings.verify_token || '',
          businessAccountId: settings.business_account_id || '',
          whatsappAccountId: settings.whatsapp_account_id || '',
          businessPhoneNumber: settings.business_phone_number || ''
        });
      }

      // Set webhook URL from API response, but force production URL
      if (waResponse.data.webhookUrl) {
        const apiWebhookUrl = waResponse.data.webhookUrl;
        // If the API returns localhost, override with production URL
        if (apiWebhookUrl.includes('localhost') || apiWebhookUrl.includes('127.0.0.1')) {
          const productionWebhookUrl = `https://leads-automater-api.nowgray.live/api/whatsapp/webhook/${currentWorkspace.id}`;
          console.log('🔧 Overriding localhost webhook URL with production:', productionWebhookUrl);
          setGeneratedWebhookUrl(productionWebhookUrl);
        } else {
          setGeneratedWebhookUrl(apiWebhookUrl);
        }
      }

      // Load Email settings
      const emailResponse = await apiClient.get('/api/settings/email', { headers });
      console.log('📧 Loaded email settings:', emailResponse.data);
      if (emailResponse.data.settings) {
        const settings = emailResponse.data.settings;
        const newEmailSettings = {
          smtpHost: settings.smtp_host || '',
          smtpPort: settings.smtp_port?.toString() || '587',
          smtpUser: settings.smtp_user || '',
          smtpPassword: settings.smtp_password || '',
          fromEmail: settings.from_email || '',
          fromName: settings.from_name || '',
          adminEmails: settings.admin_emails || ''
        };
        console.log('📧 Setting email state:', newEmailSettings);
        setEmailSettings(newEmailSettings);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (tabFromUrl === 'plans') setActiveTab('plans');
    else if (subscriptionInfo?.subscription_expired) setActiveTab('plans');
  }, [tabFromUrl, subscriptionInfo?.subscription_expired]);

  useEffect(() => {
    if (currentWorkspace) {
      loadSettings();
      generateWebhookUrl();
    }
  }, [currentWorkspace, loadSettings, generateWebhookUrl]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const [plansRes, subRes] = await Promise.all([
        apiClient.get('/api/plans'),
        apiClient.get('/api/subscription/current')
      ]);
      if (plansRes.data?.success) setPlans(plansRes.data.plans || []);
      if (subRes.data?.success) setSubscriptionCurrent(subRes.data);
    } catch (e) {
      toast.error('Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'plans') loadPlans();
  }, [activeTab, loadPlans]);

  useEffect(() => {
    if (activeTab !== 'plans' || window.Razorpay) return;
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  }, [activeTab]);

  const handleStartTrial = async (planId) => {
    try {
      setUpgradingPlanId(planId);
      const { data } = await apiClient.post('/api/subscription/create', { plan_id: planId });
      if (!data.subscription_id || !data.key) {
        toast.error(data.error || 'Payment not configured');
        setUpgradingPlanId(null);
        return;
      }
      const options = {
        key: data.key,
        subscription_id: data.subscription_id,
        name: 'Leads Automater',
        description: '7 Day Free Trial – card authorised, no charge now',
        handler: async (res) => {
          try {
            await apiClient.post('/api/subscription/verify', {
              razorpay_subscription_id: data.subscription_id
            });
            toast.success('Trial started. Your card will be charged after 7 days.');
            loadPlans();
            if (refreshWorkspaces) refreshWorkspaces();
          } catch (e) {
            toast.error(e.response?.data?.error || 'Verification failed');
          } finally {
            setUpgradingPlanId(null);
          }
        }
      };
      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Payment script not loaded');
        setUpgradingPlanId(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to start trial');
      setUpgradingPlanId(null);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      setUpgradingPlanId(planId);
      const { data } = await apiClient.post('/api/subscription/create-order', { plan_id: planId });
      if (!data.order_id || !data.key) {
        toast.error('Payment not configured');
        return;
      }
      const options = {
        key: data.key,
        amount: data.amount,
        currency: data.currency || 'INR',
        order_id: data.order_id,
        name: 'Leads Automater',
        description: 'Plan upgrade',
        handler: async (res) => {
          try {
            await apiClient.post('/api/subscription/verify-payment', {
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature
            });
            toast.success('Subscription updated');
            loadPlans();
            if (refreshWorkspaces) refreshWorkspaces();
          } catch (e) {
            toast.error(e.response?.data?.error || 'Verification failed');
          } finally {
            setUpgradingPlanId(null);
          }
        }
      };
      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        toast.error('Payment script not loaded');
        setUpgradingPlanId(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to start checkout');
      setUpgradingPlanId(null);
    }
  };

  const handleCancelSubscription = async (cancelAtPeriodEnd) => {
    const msg = cancelAtPeriodEnd
      ? 'Cancel at period end? You will keep access until the current billing period ends.'
      : 'Cancel now? You will lose access to plan benefits immediately.';
    if (!window.confirm(msg)) return;
    try {
      setCancellingSubscription(true);
      await apiClient.post('/api/subscription/cancel', { cancel_at_period_end: Boolean(cancelAtPeriodEnd) });
      toast.success(cancelAtPeriodEnd ? 'Subscription will cancel at period end' : 'Subscription cancelled');
      loadPlans();
      if (refreshWorkspaces) refreshWorkspaces();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to cancel');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const trialDaysLeft = (trialEnd) => {
    if (!trialEnd) return null;
    const end = new Date(trialEnd);
    const now = new Date();
    const diff = Math.ceil((end - now) / (24 * 60 * 60 * 1000));
    return diff > 0 ? diff : 0;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const x = typeof d === 'string' ? new Date(d) : d;
    return x.toLocaleDateString(undefined, { dateStyle: 'medium' });
  };

  const saveWhatsappSettings = async () => {
    try {
      setLoading(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      console.log('🔧 Saving WhatsApp settings with webhook URL:', generatedWebhookUrl);

      const response = await apiClient.put('/api/settings/whatsapp', {
        phone_number_id: whatsappSettings.phoneNumberId,
        access_token: whatsappSettings.accessToken,
        verify_token: whatsappSettings.verifyToken,
        business_account_id: whatsappSettings.businessAccountId,
        whatsapp_account_id: whatsappSettings.whatsappAccountId,
        business_phone_number: whatsappSettings.businessPhoneNumber,
        webhook_url: generatedWebhookUrl
      }, { headers });

      // Update webhook URL from response
      if (response.data.webhookUrl) {
        setGeneratedWebhookUrl(response.data.webhookUrl);
      }

      toast.success('WhatsApp settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save WhatsApp settings');
    } finally {
      setLoading(false);
    }
  };

  const saveEmailSettings = async () => {
    try {
      setLoading(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      const payload = {
        smtp_host: emailSettings.smtpHost,
        smtp_port: parseInt(emailSettings.smtpPort),
        smtp_user: emailSettings.smtpUser,
        smtp_password: emailSettings.smtpPassword,
        from_email: emailSettings.fromEmail,
        from_name: emailSettings.fromName,
        admin_emails: emailSettings.adminEmails
      };

      console.log('📧 Saving email settings:', payload);

      await apiClient.put('/api/settings/email', payload, { headers });
      toast.success('Email settings saved successfully!');
    } catch (error) {
      console.error('❌ Error saving email settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save email settings');
    } finally {
      setLoading(false);
    }
  };

  const testWhatsappConnection = async () => {
    try {
      setTesting(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.post('/api/settings/whatsapp/test', {}, { headers });
      toast.success(`WhatsApp connection successful! Phone: ${response.data.phoneNumber}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'WhatsApp connection failed');
    } finally {
      setTesting(false);
    }
  };

  const testEmailConnection = async () => {
    try {
      setTesting(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      await apiClient.post('/api/settings/email/test', {}, { headers });
      toast.success('Email connection successful!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Email connection failed');
    } finally {
      setTesting(false);
    }
  };

  // Password update handlers
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      setPasswordLoading(true);
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);

      if (result.success) {
        setShowPasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswords({
          current: false,
          new: false,
          confirm: false
        });
      }
    } catch (error) {
      console.error('Password update error:', error);
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetPasswordModal = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    setShowPasswordModal(false);
  };

  const isLifetimeSubscription =
    subscriptionInfo?.workspace_comped_lifetime === true ||
    subscriptionCurrent?.is_lifetime_plan === true ||
    subscriptionInfo?.is_lifetime_plan === true ||
    subscriptionCurrent?.plan?.slug === 'internal-lifetime';

  const workspaceRole = String(currentWorkspace?.user_role || '').toLowerCase();
  const isWorkspaceOwnerOrAdmin = workspaceRole === 'owner' || workspaceRole === 'admin';

  return (
    <div>
      <div className="flex items-center space-x-2 mb-6">
        <SettingsIcon className="h-6 w-6 text-gray-600" />
        <h1 className="text-2xl font-bold text-gray-900">
          Settings
          {currentWorkspace && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              - {currentWorkspace.name}
            </span>
          )}
        </h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'whatsapp'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-4 w-4" />
              <span>WhatsApp API</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'email'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4" />
              <span>Email Settings</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'password'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center space-x-2">
              <Lock className="h-4 w-4" />
              <span>Password</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'plans'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center space-x-2">
              <CreditCard className="h-4 w-4" />
              <span>Plans</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Plans tab */}
      {activeTab === 'plans' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription plans</h2>
          {plansLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {subscriptionCurrent?.plan && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-700">Current plan: {subscriptionCurrent.plan.name}</p>
                  <p className="text-sm text-gray-500">
                    Workspaces: {subscriptionCurrent.workspace_count ?? 0} / {subscriptionCurrent.max_workspaces ?? 1}
                  </p>
                  {isLifetimeSubscription && (
                    <p className="text-sm text-blue-700 mt-1">Access does not expire — no renewal date.</p>
                  )}
                  {subscriptionCurrent.subscription?.status === 'trial' && (
                    <p className="text-sm text-blue-700 mt-1">
                      Trial active – {trialDaysLeft(subscriptionCurrent.subscription.trial_end)} day(s) left
                    </p>
                  )}
                  {!isLifetimeSubscription && subscriptionCurrent.subscription?.status === 'trial' && subscriptionCurrent.subscription?.trial_end && (
                    <p className="text-sm text-gray-500">Next billing date: {formatDate(subscriptionCurrent.subscription.trial_end)}</p>
                  )}
                  {!isLifetimeSubscription && subscriptionCurrent.subscription?.status === 'active' && subscriptionCurrent.subscription?.current_period_end && (
                    <p className="text-sm text-gray-500">Next billing date: {formatDate(subscriptionCurrent.subscription.current_period_end)}</p>
                  )}
                  {subscriptionCurrent.plan.price_paise > 0 && (
                    <p className="text-sm text-gray-600 mt-0.5">
                      Plan amount: ₹{(subscriptionCurrent.plan.price_paise / 100).toFixed(0)}/{subscriptionCurrent.plan.billing_interval || 'month'}
                    </p>
                  )}
                  {(subscriptionCurrent.subscription_expired || subscriptionCurrent.subscription?.status === 'expired') && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        <p className="text-sm text-amber-800 font-medium">Your subscription has expired.</p>
                      </div>
                      <button
                        onClick={() => handleStartTrial(subscriptionCurrent.plan.id)}
                        disabled={upgradingPlanId === subscriptionCurrent.plan.id}
                        className="self-start py-2 px-4 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                      >
                        {upgradingPlanId === subscriptionCurrent.plan.id ? 'Opening...' : 'Renew subscription'}
                      </button>
                    </div>
                  )}
                  {subscriptionCurrent.subscription?.status === 'failed' && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                        <p className="text-sm text-red-800">Payment failed – please update your payment method.</p>
                      </div>
                      <button
                        onClick={() => handleStartTrial(subscriptionCurrent.plan.id)}
                        disabled={upgradingPlanId === subscriptionCurrent.plan.id}
                        className="self-start py-2 px-4 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {upgradingPlanId === subscriptionCurrent.plan.id ? 'Opening...' : 'Update payment'}
                      </button>
                    </div>
                  )}
                  {!isLifetimeSubscription && ['trial', 'active'].includes(subscriptionCurrent.subscription?.status) && subscriptionCurrent.subscription?.id && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleCancelSubscription(false)}
                        disabled={cancellingSubscription}
                        className="py-1.5 px-3 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      >
                        {cancellingSubscription ? 'Cancelling...' : 'Cancel now'}
                      </button>
                      <button
                        onClick={() => handleCancelSubscription(true)}
                        disabled={cancellingSubscription}
                        className="py-1.5 px-3 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel at period end
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!isLifetimeSubscription && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map((plan) => {
                  const isCurrentPlan = subscriptionCurrent?.plan?.id === plan.id;
                  return (
                  <div
                    key={plan.id}
                    className={`rounded-lg p-4 ${
                      isCurrentPlan
                        ? 'border-2 border-blue-500 bg-blue-50/50 shadow-md'
                        : 'border border-gray-200'
                    }`}
                  >
                    <h3 className="font-semibold text-gray-900">{plan.name}</h3>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {plan.price_paise === 0 ? 'Free' : `₹${plan.price_inr}`}
                      {plan.billing_interval && <span className="text-sm font-normal text-gray-500">/{plan.billing_interval}</span>}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{plan.max_workspaces} workspace(s)</p>
                    {plan.price_paise > 0 && (
                      <div className="mt-3 flex flex-col gap-2">
                        <button
                          onClick={() => handleStartTrial(plan.id)}
                          disabled={upgradingPlanId === plan.id}
                          className="w-full py-2 px-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {upgradingPlanId === plan.id ? 'Opening...' : 'Start Free Trial'}
                        </button>
                        <button
                          onClick={() => handleUpgrade(plan.id)}
                          disabled={upgradingPlanId === plan.id}
                          className="w-full py-2 px-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                          Pay once (no trial)
                        </button>
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="mt-2 text-xs font-medium text-blue-600">Current plan</div>
                    )}
                  </div>
                );
                })}
              </div>
              )}
            </>
          )}
        </div>
      )}

      {/* WhatsApp API Settings */}
      {activeTab === 'whatsapp' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">WhatsApp API Configuration</h2>

          {isWorkspaceOwnerOrAdmin && (
            <div className="mb-6 p-4 rounded-xl border border-emerald-200 bg-emerald-50/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
                  <Plug className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">WhatsApp setup (Meta)</p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Use Embedded Signup to connect with Facebook instead of pasting IDs here.
                  </p>
                </div>
              </div>
              <Link
                to="/setup/whatsapp"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 whitespace-nowrap shrink-0"
              >
                Open setup wizard
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number ID
              </label>
              <input
                type="text"
                value={whatsappSettings.phoneNumberId}
                onChange={(e) => handleWhatsappChange('phoneNumberId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Phone Number ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Token
              </label>
              <input
                type="password"
                value={whatsappSettings.accessToken}
                onChange={(e) => handleWhatsappChange('accessToken', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Access Token"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verify Token
              </label>
              <input
                type="text"
                value={whatsappSettings.verifyToken}
                onChange={(e) => handleWhatsappChange('verifyToken', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Verify Token"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Account ID
              </label>
              <input
                type="text"
                value={whatsappSettings.businessAccountId}
                onChange={(e) => handleWhatsappChange('businessAccountId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Business Account ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WhatsApp Account ID
              </label>
              <input
                type="text"
                value={whatsappSettings.whatsappAccountId}
                onChange={(e) => handleWhatsappChange('whatsappAccountId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter WhatsApp Account ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Phone Number
              </label>
              <input
                type="text"
                value={whatsappSettings.businessPhoneNumber}
                onChange={(e) => handleWhatsappChange('businessPhoneNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 918881371111"
              />
              <p className="mt-1 text-xs text-gray-500">
                Your WhatsApp Business phone number (with country code, no spaces or special characters)
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook URL (Auto-generated)
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={generatedWebhookUrl}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  placeholder="Webhook URL will be generated automatically"
                />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedWebhookUrl);
                    toast.success('Webhook URL copied to clipboard!');
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Copy
                </button>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p className="mb-2">
                  Copy this URL and paste it in your WhatsApp Business API webhook configuration.
                  {process.env.NODE_ENV === 'production'
                    ? ' This URL will work with your production domain.'
                    : ' For production, this will automatically use your domain name.'}
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-900 mb-1">How to configure webhook in WhatsApp Business API:</h4>
                  <ol className="text-blue-800 text-xs space-y-1 list-decimal list-inside">
                    <li>Go to your WhatsApp Business API dashboard</li>
                    <li>Navigate to Configuration → Webhooks</li>
                    <li>Paste the webhook URL above</li>
                    <li>Set the verify token to: <code className="bg-blue-100 px-1 rounded">{whatsappSettings.verifyToken || 'your_verify_token'}</code></li>
                    <li>Subscribe to message events</li>
                    <li>Save the configuration</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex space-x-3">
            <button
              onClick={testWhatsappConnection}
              disabled={testing || loading}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4" />
              )}
              <span>{testing ? 'Testing...' : 'Test Connection'}</span>
            </button>
            <button
              onClick={saveWhatsappSettings}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Email Settings */}
      {activeTab === 'email' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Email SMTP Configuration</h2>
            <button
              onClick={() => navigate('/smtp-management')}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SettingsIcon className="h-4 w-4" />
              <span>Manage Multiple SMTP</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host
              </label>
              <input
                type="text"
                value={emailSettings.smtpHost}
                onChange={(e) => handleEmailChange('smtpHost', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., smtp.gmail.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Port
              </label>
              <input
                type="text"
                value={emailSettings.smtpPort}
                onChange={(e) => handleEmailChange('smtpPort', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 587"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Username
              </label>
              <input
                type="email"
                value={emailSettings.smtpUser}
                onChange={(e) => handleEmailChange('smtpUser', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="your-email@gmail.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Password
              </label>
              <input
                type="password"
                value={emailSettings.smtpPassword}
                onChange={(e) => handleEmailChange('smtpPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter SMTP password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Email
              </label>
              <input
                type="email"
                value={emailSettings.fromEmail}
                onChange={(e) => handleEmailChange('fromEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="noreply@yourcompany.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Name
              </label>
              <input
                type="text"
                value={emailSettings.fromName}
                onChange={(e) => handleEmailChange('fromName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your Company Name"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Emails for API Reports
              </label>
              <input
                type="text"
                value={emailSettings.adminEmails}
                onChange={(e) => handleEmailChange('adminEmails', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="admin@company.com, manager@company.com, ceo@company.com"
              />
              <p className="mt-1 text-sm text-gray-500">
                Comma-separated list of email addresses to receive API usage reports
              </p>
            </div>
          </div>

          <div className="mt-6 flex space-x-3">
            <button
              onClick={testEmailConnection}
              disabled={testing || loading}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4" />
              )}
              <span>{testing ? 'Testing...' : 'Test Connection'}</span>
            </button>
            <button
              onClick={saveEmailSettings}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Password Settings */}
      {activeTab === 'password' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Password Settings</h2>

          <div className="max-w-md">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Lock className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-blue-900">Security Information</h3>
                  <p className="text-sm text-blue-800 mt-1">
                    Keep your account secure by using a strong, unique password.
                    Your password should be at least 6 characters long.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Lock className="h-4 w-4" />
              <span>Change Password</span>
            </button>
          </div>
        </div>
      )}

      {/* Password Update Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
              <button
                onClick={resetPasswordModal}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.current ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your new password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Password must be at least 6 characters long
                </p>
              </div>

              {/* Confirm New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm your new password"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {passwordData.newPassword && (
                <div className="space-y-2">
                  <div className="text-xs text-gray-600">Password strength:</div>
                  <div className="flex space-x-1">
                    <div className={`h-1 flex-1 rounded ${passwordData.newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    <div className={`h-1 flex-1 rounded ${passwordData.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    <div className={`h-1 flex-1 rounded ${/[A-Z]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    <div className={`h-1 flex-1 rounded ${/[0-9]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetPasswordModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {passwordLoading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Updating...</span>
                    </div>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
